/**
 * Centralized route configuration for the Thaklis application
 * This file contains all route paths used throughout the application
 * to ensure consistency and make maintenance easier.
 */

export const ROUTES = {
  // Admin routes
  ADMIN: {
    // System Issues Dashboard
    SYSTEM_ISSUES: '/ADM-System/issues',
    // Workflow related routes
    WORKFLOW: {
      LIST: '/ADM-Workflow/list',
      DETAIL: (id) => `/ADM-Workflow/${id}`,
      ASSIGN: (id) => `/ADM-Workflow/${id}/assign`,
      CREATE: '/admin/workflow/create',
      INSTANCE: {
        LIST: '/ADM-Workflow/instance',
        DETAIL: (id) => `/ADM-Workflow/instance/${id}`
      }
    },
    // Customer related routes
    CUSTOMER: {
      ORGANIZATIONS: '/ADM-Customer/adm-customer-organisations',
      NEW_ORGANIZATION: '/ADM-Customer/adm-new-customer-organisation',
      EDIT_ORGANIZATION: '/ADM-Customer/adm-edit-customer-organisation',
      USERS: '/ADM-Customer/adm-customer-users',
      NEW_USER: '/ADM-Customer/adm-new-customer-user',
      EDIT_USER: '/ADM-Customer/adm-edit-customer-user',
      SERVICE_MAPPING: '/ADM-Customer/adm-customer-service-mapping',
      SERVICE_MAPPINGS: '/ADM-Customer/adm-customer-service-mappings',
      ORGANIZATION_CONFIG: '/ADM-Customer/adm-customer-organization-config',
      PENDING_TICKETS: '/ADM-Customer/pending-customer-tickets'
    },
    // Partner related routes
    PARTNER: {
      ORGANIZATIONS: '/ADM-Partner/adm-partner-organisations',
      NEW_ORGANIZATION: '/ADM-Partner/adm-new-partner-organisation',
      USERS: '/ADM-Partner/adm-partner-users',
      NEW_USER: '/ADM-Partner/adm-new-partner-user',
      EDIT_USER: '/ADM-Partner/adm-edit-partner-user',
      SERVICE_MAPPING: '/ADM-Partner/adm-partner-service-mapping',
      SERVICE_MAPPINGS: '/ADM-Partner/adm-partner-service-mappings',
      PENDING_TICKETS: '/ADM-Partner/pending-tickets',
      PAYMENT: '/ADM-Partner/partner-payment'
    },
    // Channel Partner related routes
    CHANNEL_PARTNER: {
      LIST: '/ADM-ChannelPartners/adm-channel-partner-list',
      NEW: '/ADM-ChannelPartners/adm-new-channel-partner',
      DETAIL: '/ADM-ChannelPartners/adm-channel-partner-detail',
      COMMISSION_CONFIG: '/ADM-ChannelPartners/adm-commission-config',
      EDIT: '/ADM-ChannelPartners/adm-edit-channel-partner'
    },
    // Service Group related routes
    SERVICE_GROUP: {
      NEW: '/ADM-ServiceGroup/adm-new-serviceGroup',
      LIST: '/ADM-ServiceGroup/adm-serviceGroups'
    },
    // Service related routes
    SERVICE: {
      NEW: '/ADM-Service/adm-new-service',
      LIST: '/ADM-Service/adm-services'
    },
    // Dashboard routes
    DASHBOARD: {
      CUSTOMER: '/ADM-Dash/customer',
      PARTNER: '/ADM-Dash/partner'
    },
    // Invoice related routes
    INVOICE: {
      REQUESTS: '/ADM-Invoice/invoice-request'
    },
    // Wallet related routes
    WALLET: {
      TOPUP_REQUEST: '/ADM-Wallet/topup-request',
      PAYMENT_METHODS: '/ADM-Wallet/payment_methods'
    },
    // Payment Gateway routes
    PAYMENT_GATEWAY: {
      LIST: '/admin/payment-gateways',
      CREATE: '/admin/payment-gateways/create',
      DETAIL: (id) => `/admin/payment-gateways/${id}`,
      EDIT: (id) => `/admin/payment-gateways/${id}/edit`,
      CUSTOMER_CONFIG: '/admin/customer-gateway-config',
      COST_ANALYSIS: '/admin/gateway-cost-analysis',
      TOPUP_REQUESTS: '/admin/topup-requests',
    },

    // Organization Configuration routes
    ORGANIZATION_CONFIG: {
      LIST: '/admin/organization-config',
      CREATE: '/admin/organization-config/create',
      EDIT: (id) => `/admin/organization-config/edit/${id}`,
      VIEW: (id) => `/admin/organization-config/view/${id}`
    },
    // System Configuration routes
    CONFIGURATION: {
      MAIN: '/ADM-Configuration/main',
      FIELD_CATEGORIES: '/ADM-Configuration/field-categories'
    },
    // Document Configuration routes (Organization Documents)
    DOC_CONFIG: {
      MAIN: '/ADM-DocConfig/main',
      BUSINESS_CATEGORIES: '/ADM-DocConfig/business-categories'
    },
    // Organization Document Reports
    ORG_DOC_REPORTS: {
      EXPIRING: '/ADM-OrgDocReports/expiring',
      MISSING: '/ADM-OrgDocReports/missing'
    }
  },
  // Customer Organization Documents
  ORGANIZATION_DOCUMENTS: {
    LIST: '/CST-OrgDocuments/list'
  },
  // Customer Organization Profile
  ORGANIZATION_PROFILE: {
    VIEW: '/CST-Organization/profile'
  },
  // Customer Tax Services
  TAX_SERVICES: {
    LIST: '/CST-TaxServices/list'
  },
  // Customer routes
  CUSTOMER: {
    // Customer organization admin dashboard
    ADMIN_DASHBOARD: '/CST-Dashboard/admin',
    WORKFLOW: {
      REQUESTS: '/CST-Workflow/requests',
      REQUEST_DETAIL: (id) => `/CST-Workflow/requests/${id}`,
      REQUEST_DETAILS: (id) => `/CST-Workflow/requests/${id}/details`,
      REQUEST_TICKETS: (id) => `/CST-Workflow/requests/${id}/tickets`,
      NEW_REQUEST: '/CST-Workflow/new-request',
      MULTI_SERVICE_GENERATOR: '/CST-Workflow/multi-service-generator'
    },
    DASHBOARD: {
      DOCUMENTS: '/CST-Dashboard/documents',
      LEGAL: '/CST-Dashboard/legal',
      TRAINING: '/CST-Dashboard/training',
      WORKFORCE: '/CST-Dashboard/workforce',
      FINANCIAL: '/CST-Dashboard/financial',
      REPORTS: '/CST-Dashboard/reports',
      REPORT_EMAIL: '/CST-Dashboard/report-email'
    },
    // Employee management routes
    EMPLOYEES: {
      LIST: '/CST-Employees/list',
      CREATE: '/CST-Employees/create',
      DETAIL: (id) => `/CST-Employees/detail/${id}`,
      EDIT: (id) => `/CST-Employees/edit/${id}`,
      IMPORT: '/CST-Employees/import',
      DOCUMENTS: (id) => `/CST-Employees/documents/${id}`,
      DOCUMENT_PROFILE: (uid) => `/CST-Employees/document-profile/${uid}`,
      ACTIVATE_LOGIN: (id) => `/CST-Employees/detail/${id}/activate-login`
    },
    STAKEHOLDERS: {
      LIST: '/CST-Stakeholders/list',
      CREATE: '/CST-Stakeholders/create',
      DETAIL: (id) => `/CST-Stakeholders/detail/${id}`,
      EDIT: (id) => `/CST-Stakeholders/edit/${id}`,
      DOCUMENT_PROFILE: (uid) => `/CST-Stakeholders/document-profile/${uid}`,
      ACTIVATE_LOGIN: (id) => `/CST-Stakeholders/detail/${id}/activate-login`
    }
  },
  // Authentication routes
  AUTH: {
    LOGIN: '/',
    LOGOUT: '/logout',
    FORGOT_PASSWORD: '/forgot-password',
    RESET_PASSWORD: '/reset-password',
    CHANGE_PASSWORD: '/change-password'
  },
  // Customer Employee routes
  CUSTOMER_EMPLOYEE: {
    MY_TICKETS: '/my-tickets',
    PROFILE: '/profile'
  }
};
